import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Icon } from '@mui/material';
import { Typography } from '@totalsoft/rocket-ui';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation, useSubscription } from 'hooks/apollo';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useToast } from 'hooks/toasts/useToast';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import { UPDATE_FINANCIAL_PRODUCT_FILTERS } from './mutations';
import { FINANCIAL_PRODUCT_FILTERS_UPDATED } from './subscriptions';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductFiltersContainer = ({ productId, phrase, conditions, canWrite }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const isNewProduct = useMemo(() => !productId, [productId]);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Data fetching

  const { parameters, operators, loadValues, loadPagedValues } = useGenericFilterData({
    context: GenericFilterContext.FinancialProduct
  });

  // #endregion

  // #region Side effects

  useEffect(() => {
    const sanitizedConditions = (conditions || []).map(
      ({ filterId, paramId, operatorId, orderNo, filterValue, filterValueId, filterValues }) => ({
        id: filterId,
        paramId,
        operatorId,
        orderNo,
        value: filterValue,
        valueId: filterValueId,
        values: (filterValues || []).map(({ filterValue, filterValueId }) => ({ value: filterValue, valueId: filterValueId }))
      })
    );
    const data = {
      withTemplate: false,
      templateId: null,
      disabled: !canWrite,
      template: {
        phrase: phrase || null,
        conditions: sanitizedConditions
      }
    };
    resetLens(data);
    setInitialData(data);
  }, [phrase, conditions, canWrite, resetLens]);

  // #endregion

  // #region Save

  const [mutateFilters] = useMutation(UPDATE_FINANCIAL_PRODUCT_FILTERS, {
    onError: _ => {
      (lens.disabled |> set)(false);
    }
  });

  const handleSave = data => {
    (lens.disabled |> set)(true);

    const { template } = data;

    const input = { productId, ...template };

    mutateFilters({ variables: { input } });
  };

  useSubscription(FINANCIAL_PRODUCT_FILTERS_UPDATED, {
    skip: !productId,
    variables: { productId },
    onData: ({ data: { data }, client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      addToast(t('FinancialProduct.Details.Filter.FiltersSaved'), 'success');
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${data.financialProductFiltersUpdated.productId}}`
      });
      client.cache.gc();
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <Grid container flexDirection='column' gap={2} p={2}>
      {isNewProduct && (
        <Box display='flex' gap={1} alignItems='center' justifyContent='center' flex>
          <Icon fontSize='small' color='primary'>
            error
          </Icon>
          <Typography color='textPrimary'>{t('FinancialProduct.Details.PleaseCompleteGeneralData')}</Typography>
        </Box>
      )}
      {!isNewProduct && (
        <GenericFilterComposer
          lens={lens}
          dirtyInfo={dirtyInfo}
          resetLens={resetLens}
          parameters={parameters}
          operators={operators}
          loadValues={loadValues}
          loadPagedValues={loadPagedValues}
          onSave={handleSave}
          conditionsViewMode='table'
          withTemplateGeneralData
        />
      )}
    </Grid>
  );
};

FinancialProductFiltersContainer.propTypes = {
  productId: PropTypes.number,
  phrase: PropTypes.string,
  conditions: PropTypes.array,
  canWrite: PropTypes.bool
};

export default FinancialProductFiltersContainer;
